import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Grid, Stack, Tab } from "@mui/material";
import { EventMetadata, MeasurementMetadata } from "../shared/interfaces/Data";
import {
  CablePDMeasurement,
  Instrument,
  Measurement,
  MeasurementDetail,
  Result,
  TEVMeasurement,
  UHFMeasurement,
  UltraSonicMeasurement,
  VPISMeasurement,
} from "../shared/interfaces/Result";
import { HistoricUTP2Data, HistoricUTP2Events, UTP2Data, UTP2Events } from "../shared/interfaces/UTP2";

import UTP2WaveformConstants from "../shared/constants/UTP2WaveformConstants";
import { MeasurementComponent, MeasurementType, SubLocation } from "../shared/enums/Result";

import { HistogramChart } from "./Charts/HistogramChart";
import { MeasurementHistoryChart } from "./Charts/MeasurementHistoryChart";
import { PhaseResolvedChart } from "./Charts/PhaseResolvedChart";
import { WaveformChart } from "./Charts/WaveformChart";

import { MeasurementAudio } from "./MeasurementAudio";

import { CablePDMeasurementCard } from "./MeasurementCards/CablePDMeasurementCard";
import { TEVMeasurementCard } from "./MeasurementCards/TEVMeasurementCard";
import { UHFMeasurementCard } from "./MeasurementCards/UHFMeasurementCard";
import { UltraSonicMeasurementCard } from "./MeasurementCards/UltraSonicMeasurementCard";
import { VPISMeasurementCard } from "./MeasurementCards/VPISMeasurementCard";

import { useDataByCaptureAndDataPointId } from "../data/useDataByCaptureAndDataPointId";
import { useHistoricUTP2Data } from "../data/useHistoricUTP2Data";

import { useAudioFileURL } from "../data/useAudioFileURL";

import { TabContext, TabList, TabPanel } from "@mui/lab";
import { MessageCard, MessageType } from "../shared/MessageCard";
import { ReportingMeasurement } from "../shared/interfaces/ReportingMeasurement";
import { PRPSChart } from "./Charts/PRPSChart";
import { GraphControl, TDR_Default } from "./GraphControl";
import { HistoricMeasurementViewer } from "./HistoricMeasurementViewer";
import { MeasurementCommentsCard } from "./MeasurementCards/MeasurementCommentsCard";
import { MeasurementOverviewCard } from "./MeasurementOverviewCard";
import { PersistentTabPanel } from "./PersistentTabPanel";

const SINGLE_CHART_HEIGHT = "calc(70vh)";
const MULTI_CHART_HEIGHT = "calc(50vh - 100px)";

interface Props {
  measurementDetail: MeasurementDetail<Measurement>;
  selectedResult?: Result;
  measurementHistory?: MeasurementDetail<Measurement>[];
  measurementHistoryWithLatest?: MeasurementDetail<Measurement>[];
  customerId: number;
  captureId: number;
  instrument: Instrument;
  isPartialResult?: boolean;
  onChange: () => void;
  position?: number;
  isExpertOrGreater?: boolean;
  isEaEmployee?: boolean;
  reportingMeasurements?: ReportingMeasurement[];
  resultType?: string;
  selectedAssetType: string;
  onCheckboxChange?: (
    isChecked: boolean,
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    ComponentType: MeasurementComponent,
    MeasurementType: MeasurementType,
    SubLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
    chartIndex?: number,
  ) => void;
  onClearSelectedCharts?: (
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
  ) => void;
}

export enum TabsList {
  PhaseResolved = "PhaseResolved",
  UltrasonicPhaseResolved = "UltrasonicPhaseResolved",
  PRPS = "PRPS",
  UltrasonicPRPS = "UltrasonicPRPS",
  Waveforms = "Waveforms",
  Histogram = "Histogram",
  History = "History",
  Audio = "Audio",
}

export enum NumericalTabsList {
  PhaseResolved = 0,
  UltrasonicPhaseResolved = 1,
  PRPS = 2,
  UltrasonicPRPS = 3,
  Waveforms = 4,
  Histogram = 5,
  History = 6,
  Audio = 7,
}

interface DataTabs {
  key: TabsList;
  label: string;
}

export function MeasurementDetails(props: Props) {
  const { t } = useTranslation();

  const {
    measurementDetail,
    selectedResult,
    measurementHistory,
    measurementHistoryWithLatest,
    customerId,
    captureId,
    instrument,
    isPartialResult,
    position,
    isExpertOrGreater,
    isEaEmployee,
    reportingMeasurements,
    resultType,
    onClearSelectedCharts,
  } = props;

  const [waveformDataPointId, setWaveformDataPointId] = useState<number | undefined>();
  const [eventsDataPointId, setEventsDataPointId] = useState<number | undefined>();
  const [ultrasonicPhasePlotDataPointId, setUltrasonicPhasePlotDataPointId] = useState<number | undefined>();

  const [historicWaveformMetadata, setHistoricWaveformMetadata] = useState<MeasurementMetadata[]>([]);
  const [selectedHistoricWaveformMetadata, setSelectedHistoricWaveformMetadata] = useState<
    MeasurementMetadata | undefined
  >(undefined);
  const [selectedHistoricWaveform, setSelectedHistoricWaveform] = useState<HistoricUTP2Data | undefined>(undefined);

  const [historicEventsMetadata, setHistoricEventsMetadata] = useState<EventMetadata[]>([]);
  const [selectedHistoricEventsMetadata, setSelectedHistoricEventsMetadata] = useState<EventMetadata | undefined>(
    undefined,
  );
  const [selectedHistoricEvents, setSelectedHistoricEvents] = useState<HistoricUTP2Events | undefined>(undefined);

  const [historicUltrasonicPhasePlotMetadata, setHistoricUltrasonicPhasePlotMetadata] = useState<MeasurementMetadata[]>(
    [],
  );
  const [selectedHistoricUltrasonicPhasePlotMetadata, setSelectedHistoricUltrasonicPhasePlotMetadata] = useState<
    MeasurementMetadata | undefined
  >(undefined);
  const [selectedHistoricUltrasonicPhasePlot, setSelectedHistoricUltrasonicPhasePlot] = useState<
    HistoricUTP2Data | undefined
  >(undefined);

  const [waveFormChartIndex, setWaveFormChartIndex] = useState<number | undefined>(0);
  const [historicalWaveFormChartIndex, setHistoricalWaveFormChartIndex] = useState<number | undefined>(0);

  const [tabs, setTabs] = useState<DataTabs[]>([]);

  const waveforms = useDataByCaptureAndDataPointId<UTP2Data>(captureId, waveformDataPointId, position);
  const events = useDataByCaptureAndDataPointId<UTP2Events>(captureId, eventsDataPointId, position);
  const ultrasonicPhasePlots = useDataByCaptureAndDataPointId<UTP2Data>(
    captureId,
    ultrasonicPhasePlotDataPointId,
    position,
  );

  const historicWaveforms = useHistoricUTP2Data<HistoricUTP2Data>(
    customerId,
    measurementDetail,
    historicWaveformMetadata,
  );
  const historicEvents = useHistoricUTP2Data<HistoricUTP2Events>(customerId, measurementDetail, historicEventsMetadata);
  const historicUltrasonicPhasePlots = useHistoricUTP2Data<HistoricUTP2Data>(
    customerId,
    measurementDetail,
    historicUltrasonicPhasePlotMetadata,
  );

  const [audioDataPointId, setAudioDataPointId] = useState<number | undefined>();
  const [audioFilePath, setAudioFilePath] = useState<string>();
  const audioURL = useAudioFileURL(audioDataPointId, audioFilePath);

  // After moving the Data databases from SQL Server to MongoDb it added the Z suffix to dates for coordindated universal time. We need to remove this
  // so we can compare it to the date retrieved from the ReportResults table in the SQL database. Otherwise, if we convert both values to date objects
  // to compare them, when the date time is outside of daylight savings time, there will be an hours difference and it will not find a match.
  function getResultTime(time: string) {
    return time?.endsWith("Z") ? time.substring(0, time.length - 1) : time;
  }

  const selectedResultTime = selectedResult?.time && getResultTime(selectedResult?.time.toString());

  function filterReportingMeasurement(
    reportingMeasurements: ReportingMeasurement[] | undefined,
    tabPosition: NumericalTabsList,
    chartIndex?: number,
  ): ReportingMeasurement | undefined {
    if (reportingMeasurements) {
      return reportingMeasurements.find((reportingMeasurement) => {
        return (
          reportingMeasurement.chartCaptureId === captureId &&
          reportingMeasurement.chartDataPointId === selectedResult?.dataPointId &&
          selectedResult.time &&
          reportingMeasurement.chartTime === selectedResultTime &&
          reportingMeasurement.componentType === measurementDetail.measurement.ComponentType &&
          reportingMeasurement.measurementType === measurementDetail.measurementType &&
          (reportingMeasurement.subLocation ?? SubLocation.None) ===
            (measurementDetail.measurement.SubLocation ?? SubLocation.None) &&
          reportingMeasurement.panel360AssetId === measurementDetail.panel360AssetId &&
          reportingMeasurement.panelNo === measurementDetail.panelNo &&
          reportingMeasurement.panelName === measurementDetail.panelName &&
          reportingMeasurement.tabPosition === tabPosition &&
          reportingMeasurement.chartIndex === (chartIndex === undefined ? null : chartIndex)
        );
      });
    }
  }

  function filterHistoricalReportingMeasurement(
    reportingMeasurements: ReportingMeasurement[] | undefined,
    selectedHistoricMeasurement: EventMetadata | MeasurementMetadata | undefined,
    tabPosition: NumericalTabsList,
    chartIndex?: number,
  ): ReportingMeasurement | undefined {
    if (reportingMeasurements) {
      const selectedHistoricMeasurementTime =
        selectedHistoricMeasurement?.time && getResultTime(selectedHistoricMeasurement.time.toString());

      return reportingMeasurements.find((reportingMeasurement) => {
        return (
          reportingMeasurement.chartCaptureId === selectedHistoricMeasurement?.captureId &&
          reportingMeasurement.chartDataPointId === selectedHistoricMeasurement?.dataPointId &&
          selectedHistoricMeasurement.time &&
          reportingMeasurement.chartTime === selectedHistoricMeasurementTime &&
          reportingMeasurement.componentType === selectedHistoricMeasurement.measurement.ComponentType &&
          reportingMeasurement.measurementType === selectedHistoricMeasurement.measurementType &&
          (reportingMeasurement.subLocation ?? SubLocation.None) ===
            (selectedHistoricMeasurement.measurement.SubLocation ?? SubLocation.None) &&
          reportingMeasurement.panel360AssetId === measurementDetail.panel360AssetId &&
          reportingMeasurement.panelNo === measurementDetail.panelNo &&
          reportingMeasurement.panelName === measurementDetail.panelName &&
          reportingMeasurement.tabPosition === tabPosition &&
          reportingMeasurement.chartIndex === (chartIndex === undefined ? null : chartIndex)
        );
      });
    }
  }

  useEffect(() => {
    setAudioDataPointId(measurementDetail.measurement.Wav8kDataPoint);
    setAudioFilePath(measurementDetail.measurement.Wav8kFilePath);
  }, [
    measurementDetail.measurementType,
    measurementDetail.measurement.Wav8kDataPoint,
    measurementDetail.measurement.Wav8kFilePath,
  ]);

  useEffect(() => {
    if (selectedHistoricWaveformMetadata && historicWaveforms.data && historicWaveforms.data.length > 0) {
      const selectedHistoricWaveformData: HistoricUTP2Data | undefined = historicWaveforms.data.find(
        (waveform) => waveform.captureId === selectedHistoricWaveformMetadata.captureId,
      );
      selectedHistoricWaveformData && setSelectedHistoricWaveform(selectedHistoricWaveformData);
      setReflow(1);
    }
  }, [historicWaveforms.data, selectedHistoricWaveformMetadata]);

  useEffect(() => {
    if (selectedHistoricEventsMetadata && historicEvents.data && historicEvents.data.length > 0) {
      const selectedHistoricEventData: HistoricUTP2Events | undefined = historicEvents.data.find(
        (events) => events.captureId === selectedHistoricEventsMetadata.captureId,
      );
      selectedHistoricEventData && setSelectedHistoricEvents(selectedHistoricEventData);
      setReflow(2);
    }
  }, [historicEvents.data, selectedHistoricEventsMetadata]);

  useEffect(() => {
    if (
      selectedHistoricUltrasonicPhasePlotMetadata &&
      historicUltrasonicPhasePlots.data &&
      historicUltrasonicPhasePlots.data.length > 0
    ) {
      const selectedHistoricUltrasonicPhasePlotData: HistoricUTP2Data | undefined =
        historicUltrasonicPhasePlots.data.find(
          (upp) => upp.captureId === selectedHistoricUltrasonicPhasePlotMetadata.captureId,
        );
      selectedHistoricUltrasonicPhasePlotData &&
        setSelectedHistoricUltrasonicPhasePlot(selectedHistoricUltrasonicPhasePlotData);
      setReflow(3);
    }
  }, [historicUltrasonicPhasePlots.data, selectedHistoricUltrasonicPhasePlotMetadata]);

  function updateSelectedHistoricWaveform(selectedCaptureId: number | undefined) {
    const selectedHistoricalRecord = historicWaveformMetadata?.find(
      (waveform) => waveform.captureId === selectedCaptureId,
    );
    setSelectedHistoricWaveformMetadata(selectedHistoricalRecord);
  }

  function updateSelectedHistoricEvents(selectedCaptureId: number | undefined) {
    const selectedHistoricalRecord = historicEventsMetadata?.find((events) => events.captureId === selectedCaptureId);
    setSelectedHistoricEventsMetadata(selectedHistoricalRecord);
  }

  function updateSelectedHistoricUltrasonicPhasePlot(selectedCaptureId: number | undefined) {
    const selectedHistoricalRecord = historicUltrasonicPhasePlotMetadata?.find(
      (upp) => upp.captureId === selectedCaptureId,
    );
    setSelectedHistoricUltrasonicPhasePlotMetadata(selectedHistoricalRecord);
  }

  useEffect(() => {
    if (!isPartialResult) {
      const labels = [];

      if (eventsDataPointId) {
        labels.push({
          key: TabsList.PhaseResolved,
          label: t("common.phaseResolved"),
        });
        // Make sure tab defaults to the Phase resolved.
        setTab(TabsList.PhaseResolved);
      }

      if (waveformDataPointId) {
        labels.push({
          key: TabsList.Waveforms,
          label: t("common.waveforms"),
        });
      }

      if (eventsDataPointId) {
        labels.push({
          key: TabsList.Histogram,
          label: t("common.histogram"),
        });
      }

      if (ultrasonicPhasePlotDataPointId) {
        labels.push({
          key: TabsList.UltrasonicPhaseResolved,
          label: t("common.phaseResolved"),
        });
        // Make sure tab defaults to the Ultra phase resolved.
        setTab(TabsList.UltrasonicPhaseResolved);
      }

      if (isEaEmployee && eventsDataPointId) {
        labels.push({
          key: TabsList.PRPS,
          label: t("common.prps"),
        });
      }

      if (isEaEmployee && ultrasonicPhasePlotDataPointId) {
        labels.push({
          key: TabsList.UltrasonicPRPS,
          label: t("common.prps"),
        });
      }

      labels.push({
        key: TabsList.History,
        label: t("common.history"),
      });

      labels.push({
        key: TabsList.Audio,
        label: t("results.audioFiles"),
      });

      setTabs(labels);
    }
  }, [
    isPartialResult,
    waveformDataPointId,
    eventsDataPointId,
    ultrasonicPhasePlotDataPointId,
    measurementDetail.measurementType,
    t,
    isEaEmployee,
  ]);

  useEffect(() => {
    if ("WaveformsDataPoint" in measurementDetail.measurement) {
      setWaveformDataPointId(measurementDetail.measurement.WaveformsDataPoint);
    }

    if ("EventsDataPoint" in measurementDetail.measurement) {
      setEventsDataPointId(measurementDetail.measurement.EventsDataPoint);
    }

    if ("PhasePlotDataPoint" in measurementDetail.measurement) {
      setUltrasonicPhasePlotDataPointId(measurementDetail.measurement.PhasePlotDataPoint);
    }
  }, [measurementDetail.measurement]);

  useEffect(() => {
    if (measurementHistory) {
      const waveformMetadata = measurementHistory.reduce(
        (accumulator: MeasurementMetadata[] = [], historicMeasurementDetail: MeasurementDetail<Measurement>) => {
          if (historicMeasurementDetail.time && selectedResult && selectedResult.time) {
            const dateOfHistoricalMeasurement = new Date(historicMeasurementDetail.time);
            const dateOfMeasurement = new Date(selectedResult.time);

            if (dateOfHistoricalMeasurement.toISOString() === dateOfMeasurement.toISOString()) {
              return accumulator;
            }

            if ("WaveformsDataPoint" in historicMeasurementDetail.measurement && historicMeasurementDetail.captureId) {
              const measurementMetadata: MeasurementMetadata = {
                captureId: historicMeasurementDetail.captureId,
                dataPointId: historicMeasurementDetail.measurement.WaveformsDataPoint,
                time: historicMeasurementDetail.time,
                measurementType: historicMeasurementDetail.measurementType,
                measurement: historicMeasurementDetail.measurement,
              };
              return [...accumulator, measurementMetadata];
            } else return accumulator;
          } else return accumulator;
        },
        [],
      );

      setHistoricWaveformMetadata(waveformMetadata ?? []);
    }
  }, [measurementHistory, selectedResult]);

  useEffect(() => {
    if (measurementHistory) {
      const eventsMetadata = measurementHistory.reduce(
        (accumulator: EventMetadata[] = [], historicMeasurementDetail: MeasurementDetail<Measurement>) => {
          if (historicMeasurementDetail.time && selectedResult && selectedResult.time) {
            const dateOfHistoricalMeasurement = new Date(historicMeasurementDetail.time);
            const dateOfMeasurement = new Date(selectedResult.time);

            if (dateOfHistoricalMeasurement.toISOString() === dateOfMeasurement.toISOString()) {
              return accumulator;
            }

            if (
              "EventsDataPoint" in historicMeasurementDetail.measurement &&
              historicMeasurementDetail.captureId &&
              historicMeasurementDetail.instrument &&
              historicMeasurementDetail.time
            ) {
              const measurementMetadata: EventMetadata = {
                captureId: historicMeasurementDetail.captureId,
                dataPointId: historicMeasurementDetail.measurement.EventsDataPoint,
                time: historicMeasurementDetail.time,
                instrument: historicMeasurementDetail.instrument,
                measurementType: historicMeasurementDetail.measurementType,
                measurement: historicMeasurementDetail.measurement,
                insulation: historicMeasurementDetail.measurement.Insulation,
                uhfBaseline:
                  "Baseline" in historicMeasurementDetail.measurement
                    ? historicMeasurementDetail.measurement.Baseline
                    : undefined,
              };
              return [...accumulator, measurementMetadata];
            } else return accumulator;
          } else return accumulator;
        },
        [],
      );

      setHistoricEventsMetadata(eventsMetadata ?? []);
    }
  }, [measurementHistory, selectedResult]);

  useEffect(() => {
    if (measurementHistory) {
      const phasePlotMetadata = measurementHistory.reduce(
        (accumulator: MeasurementMetadata[] = [], historicMeasurementDetail: MeasurementDetail<Measurement>) => {
          if (historicMeasurementDetail.time && selectedResult && selectedResult.time) {
            const dateOfHistoricalMeasurement = new Date(historicMeasurementDetail.time);
            const dateOfMeasurement = new Date(selectedResult.time);

            if (dateOfHistoricalMeasurement.toISOString() === dateOfMeasurement.toISOString()) {
              return accumulator;
            }

            if ("PhasePlotDataPoint" in historicMeasurementDetail.measurement && historicMeasurementDetail.captureId) {
              const measurementMetadata: MeasurementMetadata = {
                captureId: historicMeasurementDetail.captureId,
                dataPointId: historicMeasurementDetail.measurement.PhasePlotDataPoint,
                time: historicMeasurementDetail.time,
                measurementType: historicMeasurementDetail.measurementType,
                measurement: historicMeasurementDetail.measurement,
              };
              return [...accumulator, measurementMetadata];
            } else return accumulator;
          } else return accumulator;
        },
        [],
      );

      setHistoricUltrasonicPhasePlotMetadata(phasePlotMetadata ?? []);
    }
  }, [measurementHistory, selectedResult]);

  function clearSelectedHistoricWaveformEventsAndMetadata() {
    setReflow(4);
    setSelectedHistoricWaveformMetadata(undefined);
    setSelectedHistoricWaveform(undefined);
  }

  function clearSelectedHistoricEventsAndMetadata() {
    setReflow(5);
    setSelectedHistoricEventsMetadata(undefined);
    setSelectedHistoricEvents(undefined);
  }

  function clearSelectedHistoricUltrasonicPhasePlotAndMetadata() {
    setReflow(6);
    setSelectedHistoricUltrasonicPhasePlotMetadata(undefined);
    setSelectedHistoricUltrasonicPhasePlot(undefined);
  }

  function handleTabChange(event: React.SyntheticEvent, newIndex: TabsList) {
    setReflow(0);
    setTab(newIndex);
  }

  function handleLatestCheckboxChange(isChecked: boolean, tabPosition: NumericalTabsList, chartIndex?: number) {
    if (props.onCheckboxChange && selectedResult && selectedResultTime) {
      props.onCheckboxChange(
        isChecked,
        selectedResult.captureId,
        selectedResult.captureId,
        selectedResult.dataPointId,
        selectedResultTime,
        tabPosition,
        measurementDetail.measurement.ComponentType,
        measurementDetail.measurement.MeasurementType,
        measurementDetail.measurement.SubLocation ?? SubLocation.None,
        measurementDetail.panel360AssetId,
        measurementDetail.panelNo,
        measurementDetail.panelName,
        chartIndex,
      );
    }
  }

  function handleClearSelectedCharts(tabPosition: NumericalTabsList) {
    if (onClearSelectedCharts && selectedResult && selectedResultTime) {
      onClearSelectedCharts(
        selectedResult.captureId,
        selectedResult.captureId,
        selectedResult.dataPointId,
        selectedResultTime,
        tabPosition,
        measurementDetail.measurement.ComponentType,
        measurementDetail.measurement.MeasurementType,
        measurementDetail.measurement.SubLocation,
        measurementDetail.panel360AssetId,
        measurementDetail.panelNo,
        measurementDetail.panelName,
      );
    }
  }

  function handleHistoricalCheckboxChange(
    isChecked: boolean,
    selectedHistoricalMeasurement: EventMetadata | MeasurementMetadata,
    tabPosition: NumericalTabsList,
    chartIndex?: number,
  ) {
    if (props.onCheckboxChange && selectedResult) {
      const selectedHistoricalMeasurementTime = getResultTime(selectedHistoricalMeasurement.time.toString());

      props.onCheckboxChange(
        isChecked,
        selectedHistoricalMeasurement.captureId,
        selectedResult.captureId,
        selectedHistoricalMeasurement.dataPointId,
        selectedHistoricalMeasurementTime,
        tabPosition,
        selectedHistoricalMeasurement.measurement.ComponentType,
        selectedHistoricalMeasurement.measurement.MeasurementType,
        selectedHistoricalMeasurement.measurement.SubLocation,
        measurementDetail.panel360AssetId,
        measurementDetail.panelNo,
        measurementDetail.panelName,
        chartIndex,
      );
    }
  }

  function handleClearHistoricalSelectedCharts(
    selectedHistoricalMeasurement: MeasurementMetadata,
    tabPosition: NumericalTabsList,
  ) {
    if (onClearSelectedCharts && selectedResult) {
      const selectedHistoricalMeasurementTime = getResultTime(selectedHistoricalMeasurement.time.toString());

      onClearSelectedCharts(
        selectedHistoricalMeasurement.captureId,
        selectedResult.captureId,
        selectedHistoricalMeasurement.dataPointId,
        selectedHistoricalMeasurementTime,
        tabPosition,
        selectedHistoricalMeasurement.measurement.ComponentType,
        selectedHistoricalMeasurement.measurement.MeasurementType,
        selectedHistoricalMeasurement.measurement.SubLocation,
        measurementDetail.panel360AssetId,
        measurementDetail.panelNo,
        measurementDetail.panelName,
      );
    }
  }

  function handleChartIndexChange(isHistorical: boolean, chartIndex?: number) {
    if (isHistorical) {
      setHistoricalWaveFormChartIndex(chartIndex);
    } else {
      setWaveFormChartIndex(chartIndex);
    }
  }

  const [phaseShift, setPhaseShift] = useState<number>(0);
  const [riseTime, setRiseTime] = useState<[number, number]>([0, 500]);
  const [pulseWidth, setPulseWidth] = useState<[number, number]>([0, 500]);

  // we default to the history tab here as this tab is unconditional and so always available.
  const [tab, setTab] = useState<TabsList>(TabsList.History);

  const [tdr, setTdr] = useState<[number, number, number]>([TDR_Default, TDR_Default, TDR_Default]);
  const [tdrId, setTdrId] = useState<number | undefined>(undefined);
  const [cableLengthUpdated, setCableLengthUpdated] = useState(false);

  const [resetGraphControl, setResetGraphControl] = useState<boolean>(false);
  const [resetPlotLines, setResetPlotLines] = useState<boolean>(false);

  function handleClearGraphControl() {
    setResetGraphControl(true);
    setResetPlotLines(true);
  }

  const [reflow, setReflow] = useState(0);

  return (
    <Grid container>
      <Grid item lg={4}>
        <Stack spacing={2} mr={2}>
          {(events.data || ultrasonicPhasePlots.data) &&
            !(tab === TabsList.Histogram || tab === TabsList.History || tab === TabsList.Audio) && (
              <GraphControl
                events={events.data}
                ultrasonicEvents={ultrasonicPhasePlots.data}
                phaseShift={phaseShift}
                setPhaseShift={setPhaseShift}
                riseTime={riseTime}
                setRiseTime={setRiseTime}
                pulseWidth={pulseWidth}
                setPulseWidth={setPulseWidth}
                measurementType={measurementDetail.measurementType}
                tab={tab}
                tdr={tdr}
                setTdr={setTdr}
                setTdrId={setTdrId}
                setCableLengthUpdated={setCableLengthUpdated}
                resetGraphControl={resetGraphControl}
                setResetGraphControl={setResetGraphControl}
              />
            )}
          <MeasurementOverviewCard
            measurementDetail={measurementDetail}
            captureId={captureId}
            selectedResult={selectedResult}
            onChange={props.onChange}
            selectedAssetType={props.selectedAssetType}
          />
          <MeasurementCommentsCard
            measurementDetail={measurementDetail}
            captureId={captureId}
            selectedResult={selectedResult}
          />

          {measurementDetail.measurementType === MeasurementType.TEV && (
            <TEVMeasurementCard measurement={measurementDetail.measurement as TEVMeasurement} />
          )}
          {measurementDetail.measurementType === MeasurementType.CablePD && (
            <CablePDMeasurementCard measurement={measurementDetail.measurement as CablePDMeasurement} />
          )}
          {measurementDetail.measurementType === MeasurementType.UHF && (
            <UHFMeasurementCard measurement={measurementDetail.measurement as UHFMeasurement} />
          )}
          {measurementDetail.measurementType === MeasurementType.Ultra && (
            <UltraSonicMeasurementCard measurement={measurementDetail.measurement as UltraSonicMeasurement} />
          )}
          {measurementDetail.measurementType === MeasurementType.VPIS && (
            <VPISMeasurementCard measurement={measurementDetail.measurement as VPISMeasurement} />
          )}
        </Stack>
      </Grid>
      <Grid item lg={8}>
        {isPartialResult ? (
          <Box display="flex" justifyContent="center" paddingTop={2}>
            <MessageCard
              title={t("results.chartsNotAvailable")}
              secondaryText={t("results.chartsWillBeAvailable")}
              messageType={MessageType.Info}
            />
          </Box>
        ) : waveforms.error ||
          events.error ||
          ultrasonicPhasePlots.error ||
          historicWaveforms.error ||
          historicEvents.error ||
          historicUltrasonicPhasePlots.error ? (
          <MessageCard
            title={t("results.chartsNotAvailable")}
            secondaryText={t("common.dataFetchError", {
              val: "$t(common.charts)",
            })}
            messageType={MessageType.Error}
          />
        ) : (
          <TabContext value={tab.toString()}>
            <TabList onChange={handleTabChange}>
              {tabs.map((tab) => {
                return <Tab value={tab.key.toString()} label={tab.label} />;
              })}
            </TabList>
            <PersistentTabPanel value={TabsList.PhaseResolved.toString()}>
              <Stack key="phaseResolvedTabContainer" spacing={1}>
                <Box
                  sx={{
                    height: selectedHistoricEvents ? MULTI_CHART_HEIGHT : SINGLE_CHART_HEIGHT,
                  }}
                  mt={1}
                >
                  <PhaseResolvedChart
                    loading={events.loading}
                    data={events.data}
                    measurementType={measurementDetail.measurementType}
                    uhfBaseline={
                      "Baseline" in measurementDetail.measurement ? measurementDetail.measurement.Baseline : undefined
                    }
                    reportingMeasurement={filterReportingMeasurement(
                      reportingMeasurements,
                      NumericalTabsList.PhaseResolved,
                    )}
                    isExpertOrGreater={isExpertOrGreater}
                    isEaEmployee={isEaEmployee}
                    onCheckboxChange={(isChecked) =>
                      handleLatestCheckboxChange(isChecked, NumericalTabsList.PhaseResolved)
                    }
                    phaseShift={phaseShift}
                    riseTime={riseTime}
                    pulseWidth={pulseWidth}
                    reflow={reflow}
                    resultType={resultType}
                  />
                </Box>

                {historicEventsMetadata && historicEventsMetadata.length > 0 && !events.loading && (
                  <HistoricMeasurementViewer
                    historicMetadataList={historicEventsMetadata}
                    selectedMetadata={selectedHistoricEventsMetadata}
                    onSelectedHistoricMetadataChange={updateSelectedHistoricEvents}
                    clearSelectedEvents={clearSelectedHistoricEventsAndMetadata}
                    chartContainerSx={{ height: MULTI_CHART_HEIGHT, px: 0 }}
                  >
                    {selectedHistoricEvents && selectedHistoricEventsMetadata && (
                      <PhaseResolvedChart
                        loading={historicEvents.loading}
                        data={selectedHistoricEvents}
                        measurementType={selectedHistoricEventsMetadata.measurementType}
                        uhfBaseline={
                          "Baseline" in selectedHistoricEventsMetadata.measurement
                            ? selectedHistoricEventsMetadata.measurement.Baseline
                            : undefined
                        }
                        reportingMeasurement={filterHistoricalReportingMeasurement(
                          reportingMeasurements,
                          selectedHistoricEventsMetadata,
                          NumericalTabsList.PhaseResolved,
                        )}
                        isExpertOrGreater={isExpertOrGreater}
                        isEaEmployee={isEaEmployee}
                        onCheckboxChange={(isChecked) =>
                          handleHistoricalCheckboxChange(
                            isChecked,
                            selectedHistoricEventsMetadata,
                            NumericalTabsList.PhaseResolved,
                          )
                        }
                        phaseShift={phaseShift}
                        riseTime={riseTime}
                        pulseWidth={pulseWidth}
                        reflow={reflow}
                        resultType={resultType}
                      />
                    )}
                  </HistoricMeasurementViewer>
                )}
              </Stack>
            </PersistentTabPanel>

            <PersistentTabPanel value={TabsList.UltrasonicPhaseResolved.toString()}>
              <Stack key="ultrasonicPhaseResolvedTabContainer" spacing={1}>
                <Box
                  sx={{
                    height: selectedHistoricUltrasonicPhasePlot ? MULTI_CHART_HEIGHT : SINGLE_CHART_HEIGHT,
                  }}
                  mt={1}
                >
                  {ultrasonicPhasePlots && (
                    <PhaseResolvedChart
                      loading={ultrasonicPhasePlots.loading}
                      data={ultrasonicPhasePlots.data}
                      measurementType={measurementDetail.measurementType}
                      reportingMeasurement={filterReportingMeasurement(
                        reportingMeasurements,
                        NumericalTabsList.UltrasonicPhaseResolved,
                      )}
                      isExpertOrGreater={isExpertOrGreater}
                      isEaEmployee={isEaEmployee}
                      onCheckboxChange={(isChecked) =>
                        handleLatestCheckboxChange(isChecked, NumericalTabsList.UltrasonicPhaseResolved)
                      }
                      phaseShift={phaseShift}
                      riseTime={riseTime}
                      pulseWidth={pulseWidth}
                      reflow={reflow}
                      resultType={resultType}
                    />
                  )}
                </Box>

                {historicUltrasonicPhasePlotMetadata &&
                  historicUltrasonicPhasePlotMetadata.length > 0 &&
                  !ultrasonicPhasePlots.loading && (
                    <HistoricMeasurementViewer
                      historicMetadataList={historicUltrasonicPhasePlotMetadata}
                      selectedMetadata={selectedHistoricUltrasonicPhasePlotMetadata}
                      onSelectedHistoricMetadataChange={updateSelectedHistoricUltrasonicPhasePlot}
                      clearSelectedEvents={clearSelectedHistoricUltrasonicPhasePlotAndMetadata}
                      chartContainerSx={{ height: MULTI_CHART_HEIGHT, px: 0 }}
                    >
                      {selectedHistoricUltrasonicPhasePlot && selectedHistoricUltrasonicPhasePlotMetadata && (
                        <PhaseResolvedChart
                          loading={historicUltrasonicPhasePlots.loading}
                          data={selectedHistoricUltrasonicPhasePlot}
                          measurementType={selectedHistoricUltrasonicPhasePlotMetadata.measurementType}
                          reportingMeasurement={filterHistoricalReportingMeasurement(
                            reportingMeasurements,
                            selectedHistoricUltrasonicPhasePlotMetadata,
                            NumericalTabsList.UltrasonicPhaseResolved,
                          )}
                          isExpertOrGreater={isExpertOrGreater}
                          isEaEmployee={isEaEmployee}
                          onCheckboxChange={(isChecked) =>
                            handleHistoricalCheckboxChange(
                              isChecked,
                              selectedHistoricUltrasonicPhasePlotMetadata,
                              NumericalTabsList.UltrasonicPhaseResolved,
                            )
                          }
                          phaseShift={phaseShift}
                          riseTime={riseTime}
                          pulseWidth={pulseWidth}
                          reflow={reflow}
                          resultType={resultType}
                        />
                      )}
                    </HistoricMeasurementViewer>
                  )}
              </Stack>
            </PersistentTabPanel>

            {isEaEmployee && (
              <TabPanel value={TabsList.PRPS.toString()} sx={{ padding: 0 }}>
                <Stack key="PRPSTabContainer" spacing={1}>
                  <Box
                    sx={{
                      height: selectedHistoricEvents ? MULTI_CHART_HEIGHT : SINGLE_CHART_HEIGHT,
                    }}
                    mt={1}
                  >
                    <PRPSChart
                      loading={events.loading}
                      data={events.data}
                      measurementType={measurementDetail.measurementType}
                      phaseShift={phaseShift}
                      riseTime={riseTime}
                      pulseWidth={pulseWidth}
                    />
                  </Box>

                  {historicEventsMetadata && historicEventsMetadata.length > 0 && !events.loading && (
                    <HistoricMeasurementViewer
                      historicMetadataList={historicEventsMetadata}
                      selectedMetadata={selectedHistoricEventsMetadata}
                      onSelectedHistoricMetadataChange={updateSelectedHistoricEvents}
                      clearSelectedEvents={clearSelectedHistoricEventsAndMetadata}
                      chartContainerSx={{
                        height: MULTI_CHART_HEIGHT,
                        padding: 0,
                      }}
                    >
                      {selectedHistoricEvents && selectedHistoricEventsMetadata && (
                        <PRPSChart
                          loading={historicEvents.loading}
                          data={selectedHistoricEvents}
                          measurementType={selectedHistoricEventsMetadata.measurementType}
                          phaseShift={phaseShift}
                          riseTime={riseTime}
                          pulseWidth={pulseWidth}
                        />
                      )}
                    </HistoricMeasurementViewer>
                  )}
                </Stack>
              </TabPanel>
            )}

            {isEaEmployee && (
              <TabPanel value={TabsList.UltrasonicPRPS.toString()} sx={{ padding: 0 }}>
                <Stack key="ultrasonicPRPSTabContainer" spacing={1}>
                  <Box
                    sx={{
                      height: selectedHistoricUltrasonicPhasePlot ? MULTI_CHART_HEIGHT : SINGLE_CHART_HEIGHT,
                    }}
                    mt={1}
                  >
                    {ultrasonicPhasePlots && (
                      <PRPSChart
                        loading={ultrasonicPhasePlots.loading}
                        data={ultrasonicPhasePlots.data}
                        measurementType={measurementDetail.measurementType}
                        phaseShift={phaseShift}
                        riseTime={riseTime}
                        pulseWidth={pulseWidth}
                      />
                    )}
                  </Box>

                  {historicUltrasonicPhasePlotMetadata &&
                    historicUltrasonicPhasePlotMetadata.length > 0 &&
                    !ultrasonicPhasePlots.loading && (
                      <HistoricMeasurementViewer
                        historicMetadataList={historicUltrasonicPhasePlotMetadata}
                        selectedMetadata={selectedHistoricUltrasonicPhasePlotMetadata}
                        onSelectedHistoricMetadataChange={updateSelectedHistoricUltrasonicPhasePlot}
                        clearSelectedEvents={clearSelectedHistoricUltrasonicPhasePlotAndMetadata}
                        chartContainerSx={{
                          height: MULTI_CHART_HEIGHT,
                          padding: 0,
                        }}
                      >
                        {selectedHistoricUltrasonicPhasePlot && selectedHistoricUltrasonicPhasePlotMetadata && (
                          <PRPSChart
                            loading={historicUltrasonicPhasePlots.loading}
                            data={selectedHistoricUltrasonicPhasePlot}
                            measurementType={selectedHistoricUltrasonicPhasePlotMetadata.measurementType}
                            phaseShift={phaseShift}
                            riseTime={riseTime}
                            pulseWidth={pulseWidth}
                          />
                        )}
                      </HistoricMeasurementViewer>
                    )}
                </Stack>
              </TabPanel>
            )}

            <TabPanel value={TabsList.Waveforms.toString()} sx={{ padding: 0 }}>
              <Stack key="waveformTabContainer" spacing={1}>
                <Box
                  sx={{
                    height: selectedHistoricWaveform ? MULTI_CHART_HEIGHT : SINGLE_CHART_HEIGHT,
                  }}
                  mt={1}
                >
                  {waveforms && (
                    <WaveformChart
                      loading={waveforms.loading}
                      data={waveforms.data?.data}
                      waveformConstants={UTP2WaveformConstants.getWaveformConstants(
                        measurementDetail.measurementType,
                        measurementDetail.measurement.WaveFormScalingFactors,
                      )}
                      reportingMeasurement={filterReportingMeasurement(
                        reportingMeasurements,
                        NumericalTabsList.Waveforms,
                        waveFormChartIndex,
                      )}
                      isExpertOrGreater={isExpertOrGreater}
                      isEaEmployee={isEaEmployee}
                      onCheckboxChange={(isChecked, chartIndex) =>
                        handleLatestCheckboxChange(isChecked, NumericalTabsList.Waveforms, chartIndex)
                      }
                      onClearSelectedCharts={() => handleClearSelectedCharts(NumericalTabsList.Waveforms)}
                      isHistorical={false}
                      onChartIndexChange={(isHistorical, chartIndex) =>
                        handleChartIndexChange(isHistorical, chartIndex)
                      }
                      tdr={tdr}
                      setTdr={setTdr}
                      tdrId={tdrId}
                      setTdrId={setTdrId}
                      cableLengthUpdated={cableLengthUpdated}
                      setCableLengthUpdated={setCableLengthUpdated}
                      reflow={reflow}
                      onClearGraphControl={() => handleClearGraphControl()}
                      resetPlotLines={resetPlotLines}
                      setResetPlotLines={setResetPlotLines}
                      resultType={resultType}
                      flatBuffer={waveforms.data?.flatBuffer}
                    />
                  )}
                </Box>
                {historicWaveformMetadata?.length > 0 && (
                  <HistoricMeasurementViewer
                    historicMetadataList={historicWaveformMetadata}
                    selectedMetadata={selectedHistoricWaveformMetadata}
                    onSelectedHistoricMetadataChange={updateSelectedHistoricWaveform}
                    clearSelectedEvents={clearSelectedHistoricWaveformEventsAndMetadata}
                    chartContainerSx={{
                      height: MULTI_CHART_HEIGHT,
                      padding: 0,
                    }}
                  >
                    {selectedHistoricWaveform && selectedHistoricWaveformMetadata && (
                      <WaveformChart
                        loading={historicWaveforms.loading}
                        data={selectedHistoricWaveform.data}
                        waveformConstants={UTP2WaveformConstants.getWaveformConstants(
                          selectedHistoricWaveformMetadata.measurementType,
                          selectedHistoricWaveformMetadata?.measurement.WaveFormScalingFactors,
                        )}
                        reportingMeasurement={filterHistoricalReportingMeasurement(
                          reportingMeasurements,
                          selectedHistoricWaveformMetadata,
                          NumericalTabsList.Waveforms,
                          historicalWaveFormChartIndex,
                        )}
                        isExpertOrGreater={isExpertOrGreater}
                        isEaEmployee={isEaEmployee}
                        onCheckboxChange={(isChecked, chartIndex) =>
                          handleHistoricalCheckboxChange(
                            isChecked,
                            selectedHistoricWaveformMetadata,
                            NumericalTabsList.Waveforms,
                            chartIndex,
                          )
                        }
                        onClearSelectedCharts={() =>
                          handleClearHistoricalSelectedCharts(
                            selectedHistoricWaveformMetadata,
                            NumericalTabsList.Waveforms,
                          )
                        }
                        isHistorical={true}
                        onChartIndexChange={(isHistorical, chartIndex) =>
                          handleChartIndexChange(isHistorical, chartIndex)
                        }
                        reflow={reflow}
                        resultType={resultType}
                        flatBuffer={selectedHistoricWaveform.flatBuffer}
                      />
                    )}
                  </HistoricMeasurementViewer>
                )}
              </Stack>
            </TabPanel>

            <TabPanel value={TabsList.Histogram.toString()} sx={{ padding: 0 }}>
              <Stack key="histogramTabContainer" spacing={1}>
                <Box
                  sx={{
                    height: selectedHistoricEvents ? MULTI_CHART_HEIGHT : SINGLE_CHART_HEIGHT,
                  }}
                  mt={1}
                >
                  {
                    <HistogramChart
                      loading={events.loading}
                      data={events.data}
                      measurementType={measurementDetail.measurementType}
                      instrument={instrument}
                      insulation={measurementDetail.measurement.Insulation}
                      uhfBaseline={
                        "Baseline" in measurementDetail.measurement ? measurementDetail.measurement.Baseline : undefined
                      }
                      reflow={reflow}
                    />
                  }
                </Box>

                {historicEventsMetadata && historicEventsMetadata.length > 0 && !events.loading && (
                  <HistoricMeasurementViewer
                    historicMetadataList={historicEventsMetadata}
                    selectedMetadata={selectedHistoricEventsMetadata}
                    onSelectedHistoricMetadataChange={updateSelectedHistoricEvents}
                    clearSelectedEvents={clearSelectedHistoricEventsAndMetadata}
                    chartContainerSx={{ height: MULTI_CHART_HEIGHT, px: 0 }}
                  >
                    {selectedHistoricEvents && selectedHistoricEventsMetadata && (
                      <HistogramChart
                        loading={historicEvents.loading}
                        data={selectedHistoricEvents}
                        measurementType={selectedHistoricEventsMetadata.measurementType}
                        instrument={selectedHistoricEventsMetadata.instrument}
                        insulation={selectedHistoricEventsMetadata.measurement.Insulation}
                        uhfBaseline={
                          "Baseline" in selectedHistoricEventsMetadata.measurement
                            ? selectedHistoricEventsMetadata.measurement.Baseline
                            : undefined
                        }
                        reflow={reflow}
                      />
                    )}
                  </HistoricMeasurementViewer>
                )}
              </Stack>
            </TabPanel>

            <TabPanel value={TabsList.History.toString()} sx={{ padding: 0 }}>
              <Box key="historyTabContainer" sx={{ height: SINGLE_CHART_HEIGHT }} mt={1}>
                <MeasurementHistoryChart
                  key="measurementHistoryChart"
                  measurementHistory={measurementHistoryWithLatest}
                  measurementType={measurementDetail.measurementType}
                />
              </Box>
            </TabPanel>

            <TabPanel value={TabsList.Audio.toString()} sx={{ padding: 0 }}>
              <MeasurementAudio
                key="measurementAudio"
                url={audioURL.data}
                loading={audioURL.isLoading}
                error={audioURL.isError}
                measurementType={measurementDetail.measurementType}
              />
            </TabPanel>
          </TabContext>
        )}
      </Grid>
    </Grid>
  );
}
